// src/api/productImages.ts

import api from '@/lib/axios';
import type { ProductImage, ImageListResponse } from '@/types/productImage';

/**
 * 上传产品图片（同名文件自动版本递增）
 * POST /api/v1/developer/products/{product_id}/images
 * @param productId - 产品 ID
 * @param file - 图片文件
 * @returns Promise<ProductImage> - 返回上传成功的图片信息
 */
export const uploadImage = (
  productId: string,
  file: File
): Promise<ProductImage> => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`/developer/products/${productId}/images`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

/**
 * 获取产品图片列表（按创建时间倒序）
 * GET /api/v1/developer/products/{product_id}/images
 * @param productId - 产品 ID
 * @returns Promise<ImageListResponse> - 包含 { images: ProductImage[] } 结构
 */
export const getImages = (productId: string): Promise<ImageListResponse> => {
  return api.get(`/developer/products/${productId}/images`);
};

/**
 * 获取单张图片详情
 * GET /api/v1/developer/products/{product_id}/images/{filename}
 * @param productId - 产品 ID
 * @param filename - 文件名
 * @returns Promise<ProductImage> - 返回图片元数据
 */
export const getImage = (
  productId: string,
  filename: string
): Promise<ProductImage> => {
  return api.get(`/developer/products/${productId}/images/${filename}`);
};

/**
 * 删除产品图片
 * DELETE /api/v1/developer/products/{product_id}/images/{filename}
 * @param productId - 产品 ID
 * @param filename - 文件名
 */
export const deleteImage = (productId: string, filename: string): Promise<void> => {
  return api.delete(`/developer/products/${productId}/images/${filename}`);
};

/**
 * 下载产品图片（直接使用 CDN 预签名 URL）
 * 注意：此函数仅用于类型安全，实际使用中直接使用 file_url 即可
 * @param fileUrl - 后端返回的 CDN URL（已包含预签名，1 小时有效期）
 */
export const downloadImage = (fileUrl: string): void => {
  // 直接在新窗口打开 CDN URL（浏览器会自动跟随 302 重定向）
  window.open(fileUrl, '_blank');
};